import { useState, useEffect } from 'react';
import { Search, TrendingUp, Clock, Award, AlertTriangle, UserX, Shield, ShieldCheck, Bell, Radar, Eye } from 'lucide-react';
import { supabase, ScamReport, ScammerReport, SCAM_CATEGORIES } from '../lib/supabase';
import ScamCard from './ScamCard';
import ScammerCard from './ScammerCard';
import NewsletterForm from './NewsletterForm';
import { CategoryBrowser, RecentInvestigations, FeaturedBlogs } from './HomeSections';

type ReportCounts = {
  [key: string]: number;
};

type HomePageProps = {
  onScamClick: (scamId: string, title?: string) => void;
  onScammerClick: (scammerId: string, title?: string) => void;
  onCategoryClick: (category: string) => void;
  onPostClick: (postId: string, slug: string) => void;
  onBlogClick: (slug: string) => void;
};

const HEADLINE_CYCLES = [
  { prefix: 'Is it a', word: 'Scam', suffix: '?' },
  { prefix: 'Is it a', word: 'Scammer', suffix: '?' },
  { prefix: 'Can you Spot a', word: 'Scam', suffix: '?' },
  { prefix: 'Can you Spot a', word: 'Scammer', suffix: '?' },
];

function SkeletonCard() {
  return (
    <div className="bg-ink-800 rounded-xl p-4 border border-line animate-pulse">
      <div className="h-4 bg-ink-700 rounded w-3/4 mb-3"></div>
      <div className="h-3 bg-ink-700 rounded w-full mb-2"></div>
      <div className="h-3 bg-ink-700 rounded w-2/3 mb-3"></div>
      <div className="h-3 bg-ink-700 rounded w-1/3"></div>
    </div>
  );
}

function SkeletonSection() {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
      <SkeletonCard />
      <SkeletonCard />
      <SkeletonCard />
    </div>
  );
}

export default function HomePage({ onScamClick, onScammerClick, onCategoryClick, onPostClick, onBlogClick }: HomePageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'scam' | 'scammer'>('all');
  const [filterCategory, setFilterCategory] = useState<string>('All');
  const [filterDate, setFilterDate] = useState<'all' | 'week' | 'month' | 'year'>('all');
  const [headlineIndex, setHeadlineIndex] = useState(0);
  const [headlineFading, setHeadlineFading] = useState(false);

  const [recentScams, setRecentScams] = useState<ScamReport[]>([]);
  const [recentScammers, setRecentScammers] = useState<ScammerReport[]>([]);

  const [topScams, setTopScams] = useState<ScamReport[]>([]);
  const [trendingScams, setTrendingScams] = useState<ScamReport[]>([]);
  const [newestScams, setNewestScams] = useState<ScamReport[]>([]);

  const [topScammers, setTopScammers] = useState<ScammerReport[]>([]);
  const [trendingScammers, setTrendingScammers] = useState<ScammerReport[]>([]);
  const [newestScammers, setNewestScammers] = useState<ScammerReport[]>([]);

  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [searchResults, setSearchResults] = useState<{
    scams: ScamReport[];
    scammers: ScammerReport[];
  } | null>(null);

  useEffect(() => {
    loadAllData();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setHeadlineFading(true);
      setTimeout(() => {
        setHeadlineIndex((i) => (i + 1) % HEADLINE_CYCLES.length);
        setHeadlineFading(false);
      }, 400);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (searchQuery.trim()) {
      performSearch();
    } else {
      setSearchResults(null);
    }
  }, [searchQuery, filterType, filterCategory, filterDate]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadRecentScams(),
        loadRecentScammers(),
        loadTopScams(),
        loadTrendingScams(),
        loadNewestScams(),
        loadTopScammers(),
        loadTrendingScammers(),
        loadNewestScammers(),
      ]);
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const getDateFilter = () => {
    if (filterDate === 'all') return null;
    const now = new Date();
    const dates = {
      week: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
      month: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000),
      year: new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000),
    };
    return dates[filterDate].toISOString();
  };

  const loadRecentScams = async () => {
    const { data } = await supabase
      .from('scam_reports')
      .select('*, profiles(username)')
      .order('created_at', { ascending: false })
      .limit(6);

    if (data) {
      setRecentScams(data);
    }
  };

  const loadRecentScammers = async () => {
    const { data } = await supabase
      .from('scammer_reports')
      .select('*, profiles(username)')
      .order('created_at', { ascending: false })
      .limit(6);

    if (data) {
      setRecentScammers(data);
    }
  };

  const loadTopScams = async () => {
    const dateFilter = getDateFilter();
    let query = supabase
      .from('scam_reports')
      .select('*, profiles(username)')
      .order('created_at', { ascending: false })
      .limit(5);

    if (dateFilter) {
      query = query.gte('created_at', dateFilter);
    }

    const { data } = await query;
    if (data) {
      const counts: ReportCounts = {};
      data.forEach((report) => {
        const key = report.title.toLowerCase();
        counts[key] = (counts[key] || 0) + 1;
      });

      const uniqueScams = Array.from(
        new Map(data.map((item) => [item.title.toLowerCase(), item])).values()
      );
      const sorted = uniqueScams.sort((a, b) => {
        const aCount = counts[a.title.toLowerCase()] || 0;
        const bCount = counts[b.title.toLowerCase()] || 0;
        return bCount - aCount;
      });

      setTopScams(sorted.slice(0, 5));
    }
  };

  const loadTrendingScams = async () => {
    const dateFilter = getDateFilter();
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

    let query = supabase
      .from('scam_reports')
      .select('*, profiles(username)')
      .gte('created_at', sevenDaysAgo)
      .order('created_at', { ascending: false })
      .limit(5);

    if (dateFilter) {
      query = query.gte('created_at', dateFilter);
    }

    const { data } = await query;
    if (data) {
      setTrendingScams(data);
    }
  };

  const loadNewestScams = async () => {
    const dateFilter = getDateFilter();
    let query = supabase
      .from('scam_reports')
      .select('*, profiles(username)')
      .order('created_at', { ascending: false })
      .limit(5);

    if (dateFilter) {
      query = query.gte('created_at', dateFilter);
    }

    const { data } = await query;
    if (data) {
      setNewestScams(data);
    }
  };

  const loadTopScammers = async () => {
    const dateFilter = getDateFilter();
    let query = supabase
      .from('scammer_reports')
      .select('*, profiles(username)')
      .order('upvote_count', { ascending: false })
      .limit(5);

    if (dateFilter) {
      query = query.gte('created_at', dateFilter);
    }

    const { data } = await query;
    if (data) {
      setTopScammers(data);
    }
  };

  const loadTrendingScammers = async () => {
    const dateFilter = getDateFilter();
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

    let query = supabase
      .from('scammer_reports')
      .select('*, profiles(username)')
      .gte('created_at', sevenDaysAgo)
      .order('upvote_count', { ascending: false })
      .limit(5);

    if (dateFilter) {
      query = query.gte('created_at', dateFilter);
    }

    const { data } = await query;
    if (data) {
      setTrendingScammers(data);
    }
  };

  const loadNewestScammers = async () => {
    const dateFilter = getDateFilter();
    let query = supabase
      .from('scammer_reports')
      .select('*, profiles(username)')
      .order('created_at', { ascending: false })
      .limit(5);

    if (dateFilter) {
      query = query.gte('created_at', dateFilter);
    }

    const { data } = await query;
    if (data) {
      setNewestScammers(data);
    }
  };

  const performSearch = async () => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) {
      setSearchResults(null);
      return;
    }

    const dateFilter = getDateFilter();

    let scamQuery = supabase
      .from('scam_reports')
      .select('*, profiles(username)')
      .or(`title.ilike.%${query}%,description.ilike.%${query}%`);

    let scammerQuery = supabase
      .from('scammer_reports')
      .select('*, profiles(username)')
      .or(
        `title.ilike.%${query}%,description.ilike.%${query}%,scammer_name.ilike.%${query}%,real_name.ilike.%${query}%`
      );

    if (filterCategory !== 'All') {
      scamQuery = scamQuery.eq('category', filterCategory);
      scammerQuery = scammerQuery.eq('category', filterCategory);
    }

    if (dateFilter) {
      scamQuery = scamQuery.gte('created_at', dateFilter);
      scammerQuery = scammerQuery.gte('created_at', dateFilter);
    }

    const [scamsResult, scammersResult] = await Promise.all([
      filterType === 'scammer' ? { data: [] } : scamQuery,
      filterType === 'scam' ? { data: [] } : scammerQuery,
    ]);

    setSearchResults({
      scams: scamsResult.data || [],
      scammers: scammersResult.data || [],
    });
  };

  return (
    <div className="min-h-screen bg-ink-900">
      {/* Hero — contained two-column panel */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 sm:mt-12 mb-6">
        <div
          className="rounded-2xl overflow-hidden border border-line shadow-xl"
          style={{ backgroundColor: '#1A1A1A' }}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            {/* Left column — copy + search */}
            <div className="p-6 sm:p-10 lg:p-12 flex flex-col justify-center text-left">
              {/* Eyebrow badge */}
              <div className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full mb-5"
                style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}
              >
                <Shield className="w-4 h-4" style={{ color: '#E8192C' }} />
                <span className="text-xs font-semibold text-white/90 tracking-wide">
                  Scam Intelligence Platform
                </span>
              </div>

              {/* Headline */}
              <h1
                className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-3 tracking-tight leading-tight"
                style={{
                  opacity: headlineFading ? 0 : 1,
                  transition: 'opacity 0.4s ease-in-out',
                }}
              >
                <span className="text-white">{HEADLINE_CYCLES[headlineIndex].prefix} </span>
                <span style={{ color: '#C4161F' }}>{HEADLINE_CYCLES[headlineIndex].word}</span>
                <span className="text-white">{HEADLINE_CYCLES[headlineIndex].suffix}</span>
              </h1>

              {/* Subheading */}
              <p className="text-sm sm:text-base text-white/70 max-w-xl mb-6">
                Find Out Now. You might be a Click away from losing your money.
              </p>

              {/* Search + filters */}
              <div className="w-full max-w-xl">
                <div className="rounded-xl border border-white/10 p-3"
                  style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40 w-4 h-4" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search by scammer name, scam type, or any details..."
                        className="w-full pl-10 pr-3 py-2.5 rounded-lg text-white text-sm placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-flag-500/50 focus:border-transparent border border-white/10"
                        style={{ backgroundColor: '#0D0D0D' }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <select
                      aria-label="Filter by type"
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value as typeof filterType)}
                      className="px-3 py-2 rounded-lg focus:ring-2 focus:ring-flag-500/50 focus:border-transparent text-white text-sm border border-white/10"
                      style={{ backgroundColor: '#0D0D0D' }}
                    >
                      <option value="all">All Types</option>
                      <option value="scam">Scams Only</option>
                      <option value="scammer">Scammers Only</option>
                    </select>

                    <select
                      aria-label="Filter by category"
                      value={filterCategory}
                      onChange={(e) => setFilterCategory(e.target.value)}
                      className="px-3 py-2 rounded-lg focus:ring-2 focus:ring-flag-500/50 focus:border-transparent text-white text-sm border border-white/10"
                      style={{ backgroundColor: '#0D0D0D' }}
                    >
                      <option value="All">All Categories</option>
                      {SCAM_CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>

                    <select
                      aria-label="Filter by date"
                      value={filterDate}
                      onChange={(e) => setFilterDate(e.target.value as typeof filterDate)}
                      className="px-3 py-2 rounded-lg focus:ring-2 focus:ring-flag-500/50 focus:border-transparent text-white text-sm border border-white/10"
                      style={{ backgroundColor: '#0D0D0D' }}
                    >
                      <option value="all">All Time</option>
                      <option value="week">Last Week</option>
                      <option value="month">Last Month</option>
                      <option value="year">Last Year</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Right column — illustrative graphic */}
            <div
              className="relative flex items-center justify-center p-8 sm:p-12 min-h-[280px] lg:min-h-[420px] overflow-hidden"
              style={{ backgroundColor: '#0D0D0D' }}
            >
              {/* Radial glow */}
              <div
                className="absolute inset-0 opacity-60"
                style={{
                  background:
                    'radial-gradient(circle at 50% 45%, rgba(196,22,31,0.18) 0%, transparent 65%)',
                }}
              />

              {/* Scan ring animation */}
              <div className="relative z-10 w-full max-w-sm">
                {/* Outer ring */}
                <div className="relative mx-auto w-44 h-44 sm:w-52 sm:h-52 mb-6">
                  <div
                    className="absolute inset-0 rounded-full border-2 animate-spin-slow"
                    style={{ borderColor: 'rgba(232,25,44,0.25)', borderTopColor: '#E8192C' }}
                  />
                  <div
                    className="absolute inset-3 rounded-full border animate-spin-reverse"
                    style={{ borderColor: 'rgba(255,255,255,0.06)', borderBottomColor: 'rgba(255,255,255,0.25)' }}
                  />
                  {/* Center shield */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div
                      className="w-20 h-20 rounded-2xl flex items-center justify-center shadow-lg"
                      style={{ backgroundColor: '#1A1A1A', border: '1px solid rgba(232,25,44,0.3)' }}
                    >
                      <ShieldCheck className="w-10 h-10" style={{ color: '#E8192C' }} />
                    </div>
                  </div>
                </div>

                {/* Alert card stack */}
                <div className="space-y-2.5">
                  <div
                    className="rounded-lg px-4 py-3 flex items-center gap-3 animate-slide-up"
                    style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'rgba(232,25,44,0.15)' }}
                    >
                      <AlertTriangle className="w-4 h-4" style={{ color: '#E8192C' }} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-white truncate">Phishing Domain Detected</p>
                      <p className="text-[10px] text-white/50 truncate">secure-paypal-verify.com</p>
                    </div>
                    <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
                      style={{ backgroundColor: 'rgba(232,25,44,0.15)', color: '#FF2D3F' }}
                    >
                      HIGH RISK
                    </span>
                  </div>

                  <div
                    className="rounded-lg px-4 py-3 flex items-center gap-3 animate-slide-up-delayed"
                    style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}
                    >
                      <UserX className="w-4 h-4 text-white/70" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-white truncate">Scammer Reported</p>
                      <p className="text-[10px] text-white/50 truncate">@crypto_whalez_KE</p>
                    </div>
                    <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap text-white/70"
                      style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}
                    >
                      VERIFIED
                    </span>
                  </div>

                  <div
                    className="rounded-lg px-4 py-3 flex items-center gap-3 animate-slide-up-delayed-2"
                    style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'rgba(34,197,94,0.12)' }}
                    >
                      <ShieldCheck className="w-4 h-4 text-sage-500" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-white truncate">Domain Scan Complete</p>
                      <p className="text-[10px] text-white/50 truncate">safaricom.co.ke</p>
                    </div>
                    <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap text-sage-500"
                      style={{ backgroundColor: 'rgba(34,197,94,0.12)' }}
                    >
                      SAFE
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trust indicators row */}
        <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { icon: ShieldCheck, label: 'Community Verified' },
            { icon: Radar, label: 'AI-Powered Detection' },
            { icon: Bell, label: 'Real-Time Alerts' },
            { icon: Eye, label: 'Scam Tracking' },
          ].map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="flex items-center gap-2.5 rounded-xl border border-line px-4 py-3 bg-ink-800 hover:border-flag-500/40 transition-colors"
            >
              <Icon className="w-4 h-4 text-flag-500 flex-shrink-0" />
              <span className="text-xs font-semibold text-snow">{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Browse by Category */}
      <CategoryBrowser onCategoryClick={onCategoryClick} />

      {/* Newsletter Banner */}
      <div className="bg-ink-800 border-y border-line" style={{ minHeight: '56px' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3">
          <NewsletterForm source="homepage_banner" variant="compact" />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {initialLoad ? (
          <div className="space-y-8">
            <section>
              <h2 className="sf-heading text-base font-semibold text-snow mb-4 inline-flex items-center gap-2">
                <Clock className="w-4 h-4 text-flag-500" />
                Recent Reports
              </h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-snow mb-3 inline-flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4 text-flag-500" />
                    Latest Scams
                  </h3>
                  <SkeletonSection />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-snow mb-3 inline-flex items-center gap-1">
                    <UserX className="w-4 h-4 text-flag-500" />
                    Latest Scammers
                  </h3>
                  <SkeletonSection />
                </div>
              </div>
            </section>
          </div>
        ) : (
        <>
        {!searchResults && (
          <div className="space-y-8 mb-8">
            <section>
              <h2 className="sf-heading text-base font-semibold text-snow mb-4 inline-flex items-center gap-2">
                <Clock className="w-4 h-4 text-flag-500" />
                Recent Reports
              </h2>

              <div className="space-y-4">
                {recentScams.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-snow mb-3 inline-flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4 text-flag-500" />
                      Latest Scams
                    </h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {recentScams.slice(0, 3).map((scam) => (
                        <ScamCard key={scam.id} report={scam} onClick={() => onScamClick(scam.id, scam.title)} />
                      ))}
                    </div>
                  </div>
                )}

                {recentScammers.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-snow mb-3 inline-flex items-center gap-1">
                      <UserX className="w-4 h-4 text-flag-500" />
                      Latest Scammers
                    </h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {recentScammers.slice(0, 3).map((scammer) => (
                        <ScammerCard key={scammer.id} report={scammer} onClick={() => onScammerClick(scammer.id, scammer.title)} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>
          </div>
        )}

        {searchResults ? (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-snow mb-4">
                Search Results ({searchResults.scams.length + searchResults.scammers.length})
              </h2>

              {searchResults.scams.length === 0 && searchResults.scammers.length === 0 ? (
                <div className="bg-ink-800 rounded-xl shadow p-8 text-center border border-line">
                  <Search className="w-8 h-8 text-ash-400 mx-auto mb-3" />
                  <p className="text-sm text-ash-400">No results found. Try different search terms.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {searchResults.scammers.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-snow mb-3 inline-flex items-center gap-1">
                        <UserX className="w-4 h-4 text-flag-500" />
                        Scammers ({searchResults.scammers.length})
                      </h3>
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {searchResults.scammers.map((scammer) => (
                          <ScammerCard key={scammer.id} report={scammer} onClick={() => onScammerClick(scammer.id, scammer.title)} />
                        ))}
                      </div>
                    </div>
                  )}

                  {searchResults.scams.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-snow mb-3 inline-flex items-center gap-1">
                        <AlertTriangle className="w-4 h-4 text-flag-500" />
                        Scams ({searchResults.scams.length})
                      </h3>
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {searchResults.scams.map((scam) => (
                          <ScamCard key={scam.id} report={scam} onClick={() => onScamClick(scam.id, scam.title)} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            <section>
              <h2 className="sf-heading text-base font-semibold text-snow mb-4 inline-flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-flag-500" />
                Scams
              </h2>

              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-snow mb-3 inline-flex items-center gap-1">
                    <Award className="w-4 h-4 text-flag-500" />
                    Most Reported Scams
                  </h3>
                  {topScams.length === 0 ? (
                    <p className="text-sm text-ash-400 bg-ink-800 rounded-lg p-4 border border-line">No scams reported yet.</p>
                  ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {topScams.map((scam) => (
                        <ScamCard key={scam.id} report={scam} onClick={() => onScamClick(scam.id, scam.title)} />
                      ))}
                    </div>
                  )}
                </div>

                <div className="sf-divider border-t border-line pt-4">
                  <h3 className="text-sm font-semibold text-snow mb-3 inline-flex items-center gap-1">
                    <TrendingUp className="w-4 h-4 text-flag-500" />
                    Trending Scams
                  </h3>
                  {trendingScams.length === 0 ? (
                    <p className="text-sm text-ash-400 bg-ink-800 rounded-lg p-4 border border-line">No trending scams.</p>
                  ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {trendingScams.map((scam) => (
                        <ScamCard key={scam.id} report={scam} onClick={() => onScamClick(scam.id, scam.title)} />
                      ))}
                    </div>
                  )}
                </div>

                <div className="sf-divider border-t border-line pt-4">
                  <h3 className="text-sm font-semibold text-snow mb-3 inline-flex items-center gap-1">
                    <Clock className="w-4 h-4 text-flag-500" />
                    Newest Scams
                  </h3>
                  {newestScams.length === 0 ? (
                    <p className="text-sm text-ash-400 bg-ink-800 rounded-lg p-4 border border-line">No recent scams.</p>
                  ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {newestScams.map((scam) => (
                        <ScamCard key={scam.id} report={scam} onClick={() => onScamClick(scam.id, scam.title)} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </section>

            <section>
              <h2 className="sf-heading text-base font-semibold text-snow mb-4 inline-flex items-center gap-2">
                <UserX className="w-4 h-4 text-flag-500" />
                Scammers
              </h2>

              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-snow mb-3 inline-flex items-center gap-1">
                    <Award className="w-4 h-4 text-flag-500" />
                    Most Reported Scammers
                  </h3>
                  {topScammers.length === 0 ? (
                    <p className="text-sm text-ash-400 bg-ink-800 rounded-lg p-4 border border-line">
                      No scammers reported yet.
                    </p>
                  ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {topScammers.map((scammer) => (
                        <ScammerCard key={scammer.id} report={scammer} onClick={() => onScammerClick(scammer.id, scammer.title)} />
                      ))}
                    </div>
                  )}
                </div>

                <div className="sf-divider border-t border-line pt-4">
                  <h3 className="text-sm font-semibold text-snow mb-3 inline-flex items-center gap-1">
                    <TrendingUp className="w-4 h-4 text-flag-500" />
                    Trending Scammers
                  </h3>
                  {trendingScammers.length === 0 ? (
                    <p className="text-sm text-ash-400 bg-ink-800 rounded-lg p-4 border border-line">No trending scammers.</p>
                  ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {trendingScammers.map((scammer) => (
                        <ScammerCard key={scammer.id} report={scammer} onClick={() => onScammerClick(scammer.id, scammer.title)} />
                      ))}
                    </div>
                  )}
                </div>

                <div className="sf-divider border-t border-line pt-4">
                  <h3 className="text-sm font-semibold text-snow mb-3 inline-flex items-center gap-1">
                    <Clock className="w-4 h-4 text-flag-500" />
                    Newest Scammers
                  </h3>
                  {newestScammers.length === 0 ? (
                    <p className="text-sm text-ash-400 bg-ink-800 rounded-lg p-4 border border-line">No recent scammers.</p>
                  ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {newestScammers.map((scammer) => (
                        <ScammerCard key={scammer.id} report={scammer} onClick={() => onScammerClick(scammer.id, scammer.title)} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Recent Scam Investigations */}
            <RecentInvestigations onPostClick={onPostClick} />

            {/* Featured Blogs */}
            <FeaturedBlogs onBlogClick={onBlogClick} />
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}
